import { StatelessComponent, FC, ReactElement } from 'react'
import * as styles from 'components/features/pfm/DeviceSettingsModule/DeviceSettingsModule.css'
import { formatUSDate } from 'helpers/dateTimeHelpers'
import { Device, UserAgent } from 'interfaces/auth'
import { LAST_LOGIN_LOCATION_HINT } from 'constants/hints'
import TextButton from 'components/atoms/buttons/TextButton'
import { BodyText } from 'components/atoms/Text'
import ConfirmModalComponent from 'components/features/pfm/ConfirmModal/ConfirmModal'
import UserAgentBadge from 'components/atoms/UserAgentBadge'
import AbsentValue from 'components/atoms/AbsentValue'
import Table, { SAME_SORTING_SELECTOR, TableColumnDefinition, TableComponentType } from 'components/molecules/tables/Table'
import { SortingProp } from 'interfaces/table'

export type DeviceSettingsStateProps = {
  devices: Device[]
  deletingId: null | number
}

export type DeviceSettingsDispatchProps = {
  showRemoveDeviceModal: (deviceId: number) => void
  hideModal: () => void
  removeDevice: (deviceId: number) => void
}

type DeviceSettingsProps = DeviceSettingsStateProps & DeviceSettingsDispatchProps

type NullableDateComponentProps = {
  date: string | null
  absentCopy: string
}
const NullableDateComponent: FC<NullableDateComponentProps> = ({ date, absentCopy }): ReactElement<any> =>
  date !== null
    ? <span>{ formatUSDate(date) }</span>
    : <AbsentValue children={ absentCopy } />

const DEVICE_NAME_COLUMN: TableColumnDefinition<Device, UserAgent> = {
  name: 'name',
  title: 'Device',
  component: UserAgentBadge,
  propsSelector: ({ user_agent }) => user_agent,
  sortingSelector: ({ user_agent }) => user_agent.string,
}

const DATE_VERIFIED_COLUMN: TableColumnDefinition<Device, string> = {
  name: 'verified_at',
  title: 'Date Verified',
  component: (date) => <NullableDateComponent date={ date } absentCopy='Not verified yet' />,
  propsSelector: ({ verified_at }) => verified_at,
  sortingSelector: SAME_SORTING_SELECTOR,
}

const LAST_LOGIN_DATE_COLUMN: TableColumnDefinition<Device, string | null> = {
  name: 'last_login_at',
  title: 'Last Login Date',
  component: (date) => <NullableDateComponent date={ date } absentCopy='Not logged in yet' />,
  propsSelector: ({ last_login_at }) => last_login_at,
  sortingSelector: SAME_SORTING_SELECTOR,
}

const LAST_LOGIN_LOCATION_COLUMN: TableColumnDefinition<Device, string> = {
  name: 'logged_in_location',
  title: 'Last Login Location',
  helpText: LAST_LOGIN_LOCATION_HINT,
  component: (location) => location ? location : <AbsentValue children='Not detected' />,
  propsSelector: ({ location }) => location,
  sortingSelector: SAME_SORTING_SELECTOR,
}

const TypedTable = Table as TableComponentType<Device>

const DeviceSettingsModule: StatelessComponent<DeviceSettingsProps> = (props) => {
  const {
    devices,
    deletingId,
    showRemoveDeviceModal, hideModal, removeDevice,
  } = props

  if (devices.length === 0) {
    return (
      <BodyText className={ styles.emptyState }>
        You have no linked devices.
      </BodyText>
    )
  }

  const DELETE_BUTTON: TableColumnDefinition<Device, Device> = {
    name: 'delete',
    title: '',
    component: (device) => (
      <TextButton onClick={ () => showRemoveDeviceModal(device.id) } qaMarker='delete-button'>
        Delete
      </TextButton>
    ),
  }

  const COLUMNS: TableColumnDefinition<Device, any>[] = [
    DEVICE_NAME_COLUMN,
    DATE_VERIFIED_COLUMN,
    LAST_LOGIN_DATE_COLUMN,
    LAST_LOGIN_LOCATION_COLUMN,
    DELETE_BUTTON,
  ]

  const sorting: SortingProp = {
    direction: 'desc',
    columnName: 'last_login_at',
  }

  return (
    <section>
      { deletingId !== null && (
        <ConfirmModalComponent onCancel={ hideModal } onConfirm={ () => removeDevice(deletingId) } confirmButtonText='Delete'>
          Deleting this device will remove it from the verified list. It will need to be verified again via email for any future logins. Are you sure you want to delete this device?
        </ConfirmModalComponent>
      )}
      <TypedTable columns={ COLUMNS } rows={ devices } sorting={ sorting } className={ styles.deviceTable } />
    </section>
  )
}

export default DeviceSettingsModule
